import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { addFavouriteDashboard, FavouriteDashboard, getFavouriteDashboards, removeFavouriteDashboard } from "../api/favouriteDashboardsApi";
import { SearchableDashboard } from "../constants";

const FAVOURITES_QUERY_KEY = ["favourite-dashboards"];

// GET/POST/DELETE /user_service/favourites are live (docs/needed-endpoints.md §2) - replaces the
// earlier localStorage mock. Shared by the Favourites page, CedarHome, and DashboardCard's own
// star toggle via this one hook - backed by react-query's cache so every subscriber updates
// immediately when any of them mutates, same reasoning as before this was a real endpoint.
//
// This hook's own external shape (favouriteIds as .key strings, isFavourite(key)) is unchanged for
// CedarHome/FavouritesPage, which filter the dashboard registry by .key - GET's response
// conveniently joins the full dashboard object, so .key is always available to derive from it
// without a second lookup. toggleFavourite takes the whole dashboard (not just its key) because
// the real add/remove endpoints key off the dashboard's numeric id, not its key.
export const useFavouriteDashboards = () => {
  const queryClient = useQueryClient();
  const { data } = useQuery<FavouriteDashboard[]>(FAVOURITES_QUERY_KEY, getFavouriteDashboards, { staleTime: 0 });
  const favourites = data ?? [];
  // Defensive against a malformed cache entry (see addMutation's onSuccess comment below) ever
  // crashing every consumer of this hook - .dashboard.key would otherwise throw on any entry
  // that isn't a real, fully-shaped FavouriteDashboard.
  const favouriteIds = favourites.filter((favourite) => favourite?.dashboard).map((favourite) => favourite.dashboard.key);

  const addMutation = useMutation((dashboard: SearchableDashboard) => addFavouriteDashboard(dashboard.id), {
    onSuccess: (favourite) => {
      // Belt-and-suspenders, not the primary fix (see addFavouriteDashboard's own comment and
      // toggleFavourite's below for the actual root cause/fix): a cancelled request resolves to
      // null (handleError's isCancelError branch), and the documented "200 idempotent no-op if
      // already favourited" response isn't guaranteed to carry the same joined shape a genuine
      // create does either. Guard against pushing a null/malformed entry into the cache regardless
      // of which of those produced it - correct either way, since neither case has anything real
      // to add.
      if (!favourite?.dashboard) return;
      queryClient.setQueryData<FavouriteDashboard[]>(FAVOURITES_QUERY_KEY, (current) => [...(current ?? []), favourite]);
    },
  });

  const removeMutation = useMutation((dashboard: SearchableDashboard) => removeFavouriteDashboard(dashboard.id), {
    onSuccess: (_result, dashboard) => {
      queryClient.setQueryData<FavouriteDashboard[]>(FAVOURITES_QUERY_KEY, (current) =>
        (current ?? []).filter((favourite) => favourite?.dashboard_id !== dashboard.id)
      );
    },
  });

  const isFavourite = useCallback((dashboardKey: string) => favouriteIds.includes(dashboardKey), [favouriteIds]);

  // Guards repeated clicks on THIS SAME star specifically (isLoading is scoped per DashboardCard,
  // since each renders its own instance of this hook) - isFavourite's own answer doesn't update
  // until the in-flight mutation's response comes back, so without this, clicking the same star
  // again before then fires a second, redundant mutation for the same dashboard. The actual crash
  // report ("clicking several different favourite stars in quick succession") is a distinct
  // mechanism this doesn't cover on its own - see addFavouriteDashboard's own comment
  // (favouriteDashboardsApi.ts) for the real root cause across DIFFERENT dashboards' own separate
  // hook instances, which needed its own fix there (a per-dashboard cancel-token key).
  const toggleFavourite = useCallback(
    (dashboard: SearchableDashboard) => {
      if (addMutation.isLoading || removeMutation.isLoading) return;
      if (isFavourite(dashboard.key)) {
        removeMutation.mutate(dashboard);
      } else {
        addMutation.mutate(dashboard);
      }
    },
    [isFavourite, addMutation, removeMutation]
  );

  return { favouriteIds, isFavourite, toggleFavourite };
};
